import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from 'src/app-redux/rtkq.interceptor';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import { apiResources } from 'src/common/Constants';
import { type TLocation } from 'src/management/Locations/Locations';

const locationsTag = 'locations';

/** RTK Query API slice for location management endpoints. */
export const locationsApi = createApi({
  reducerPath: 'locationsApi',
  tagTypes: [locationsTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch all locations. */
    fetchLocations: builder.query<Array<TLocation>, void>({
      providesTags: [locationsTag],
      query: () => apiResources.location,
      ...transformResponses,
    }),
    /** Query to fetch a single location by ID. */
    getLocation: builder.query<TLocation, string>({
      providesTags: [locationsTag],
      query: (id) => ({ url: apiResources.location + `/${id}`, method: 'GET' }),
      ...transformResponses,
    }),
    /** Mutation to create a new location. */
    createLocation: builder.mutation<TLocation, Omit<TLocation, 'id'>>({
      query: (body) => ({ url: apiResources.location, method: 'POST', body }),
      invalidatesTags: (_result, error) => (error ? [] : [locationsTag]),
      ...transformResponses,
    }),
    /** Mutation to update an existing location. */
    updateLocation: builder.mutation<TLocation, TLocation>({
      query: ({ id, ...patch }) => ({ url: apiResources.location + `/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_result, error) => (error ? [] : [locationsTag]),
      ...transformResponses,
    }),
    /** Mutation to delete a location by ID. */
    deletedLocation: builder.mutation<TLocation, number>({
      query: (id) => ({ url: apiResources.location + `/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, error) => (error ? [] : [locationsTag]),
      ...transformResponses,
    }),
  }),
});

/** RTK Query hooks for location management endpoints. */
export const {
  useFetchLocationsQuery,
  useGetLocationQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeletedLocationMutation,
} = locationsApi;
